import { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { Layout, Typography } from 'antd';
import {
  CarOutlined, HomeOutlined, IdcardOutlined, SearchOutlined, TeamOutlined,
} from '@ant-design/icons';

/**
 * Kiosk shell — the gate device surface.
 * Camera-first, high-contrast, big tap targets. Assumes always-online.
 * Real camera + QR pipeline arrives with the device build; for now the
 * scan flow accepts pasted / typed pass codes.
 */
export function KioskShell() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const shift = readShift();
  return (
    <Layout style={{ minHeight: 'calc(100vh - 50px)', background: '#0b1420', color: '#e6ebf3' }}>
      <div style={{
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: '1px solid #1e2a3d', background: '#0f1a2b',
      }}>
        <Typography.Text style={{ color: '#8fa3c0', fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>
          Gate kiosk
        </Typography.Text>
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 13 }}>
          {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div style={{ fontSize: 11.5, color: shift ? '#8fa3c0' : '#f4a3a0' }}>
          {shift ? `Shift · ${shift.name} · from ${shift.startedAt.slice(11, 16)}` : 'No shift signed in'}
        </div>
      </div>
      <Layout.Content style={{ padding: '18px 18px 84px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
        <Outlet />
      </Layout.Content>
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-around',
        background: '#0f1a2b', borderTop: '1px solid #1e2a3d',
        padding: '10px 0', zIndex: 100,
      }}>
        <Tab to={`/i/${institution}/gate`} end icon={<HomeOutlined />} label="Idle" />
        <Tab to={`/i/${institution}/gate/scan`} icon={<IdcardOutlined />} label="Scan" />
        <Tab to={`/i/${institution}/gate/lookup`} icon={<SearchOutlined />} label="Lookup" />
        <Tab to={`/i/${institution}/gate/vehicles`} icon={<CarOutlined />} label="Vehicles" />
        <Tab to={`/i/${institution}/gate/shift`} icon={<TeamOutlined />} label="Shift" />
      </nav>
    </Layout>
  );
}

function Tab({ to, icon, label, end }: { to: string; icon: React.ReactNode; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end}
      style={({ isActive }) => ({
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        textDecoration: 'none',
        color: isActive ? '#7ab6ff' : '#8fa3c0',
        fontSize: 11, fontWeight: isActive ? 700 : 500,
      })}
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
      {label}
    </NavLink>
  );
}

/* Shift persisted in localStorage (device-local, per O-01 idea). */
export interface ShiftState { name: string; startedAt: string; }
const SHIFT_KEY = 'velora.gate.shift';

export function readShift(): ShiftState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SHIFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ShiftState;
  } catch { return null; }
}
export function signInShift(name: string): ShiftState {
  const s: ShiftState = { name, startedAt: new Date().toISOString() };
  window.localStorage.setItem(SHIFT_KEY, JSON.stringify(s));
  window.dispatchEvent(new CustomEvent('velora:gate-shift'));
  return s;
}
export function signOutShift(): void {
  window.localStorage.removeItem(SHIFT_KEY);
  window.dispatchEvent(new CustomEvent('velora:gate-shift'));
}
